/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fpLogging, fplsFetch } from '../../../../../shared/utils';

export const defaultState = {
  error: '',
  folders: [],
  formId: null,
  isError: false,
  processInfo: '',
  isLoading: false,
  isProcessing: false,
  activeDownloads: {},
  expandedFolder: null,
  pageSize: {
    files: 10,
    folders: 10,
  },
};

const failedGetMsg =
  'An error has occurred when attempting to ' +
  'retrieve files on this form. Please refresh & try again. You may also send ' +
  'an email to support@formpl.us if this persists';

export const fetchFormResponsesFolders = createAsyncThunk(
  'fileManager/fetchFormResponsesFoldersById',
  async (formId) => {
    const response = await fplsFetch({ url: `/api/v2/user/forms/${formId}/folders` });
    return response;
  }
);

const fileManagerSlice = createSlice({
  name: 'fileManager',
  initialState: defaultState,
  reducers: {
    fetchFilesStart: (state) => {
      state.isLoading = true;
      state.isError = false;
    },
    fetchFilesFail: (state) => {
      state.isLoading = false;
      state.isError = true;
      state.expandedFolder = null;
    },
    fetchFilesSuccess: (state, { payload }) => {
      state.isLoading = false;
      state.isError = false;
      state.expandedFolder = payload;
    },
    processStart: (state, { payload }) => {
      state.isProcessing = true;
      state.processInfo = payload;
    },
    processEnd: (state) => {
      state.isProcessing = false;
      state.processInfo = '';
    },
    processError: (state) => {
      state.isError = true;
      state.error = 'An error occured';
    },
    clearError: (state) => {
      state.isError = false;
      state.error = '';
    },
    collapseFolder: (state) => {
      const { folders, expandedFolder } = state;
      const updatedFolders = folders.map((f) =>
        f.id === expandedFolder.id ? { ...f, files_count: expandedFolder.files_count } : f
      );

      state.isLoading = false;
      state.folders = updatedFolders;
      state.expandedFolder = null; // finally
    },
    changePageSize: (state, { payload }) => {
      const { type, value } = payload;
      state.pageSize[type] = value;
    },
    createFolder: (state, { payload }) => {
      state.folders.unshift(payload);
    },
    renameFile: (state, { payload }) => {
      const { expandedFolder } = state;

      expandedFolder.files = expandedFolder.files.map((file) => {
        if (file.id === payload.id) {
          return payload;
        }
        return file;
      });
    },
    renameFolder: (state, { payload }) => {
      const { folders } = state;
      const { name, id } = payload;

      folders.find((f) => f.id === id).name = name;
    },
    moveSingleFileToFolder: (state, { payload }) => {
      const { expandedFolder, folders } = state;
      const { parentId, destinationId, movedEntities } = payload;

      const file = movedEntities[0];
      const parentFolder = folders.find((f) => f.id === parentId);
      const destinationFolder = folders.find((f) => f.id === destinationId);

      expandedFolder.files = expandedFolder.files.filter((f) => f.id !== file.id);
      parentFolder.files_count--;
      expandedFolder.files_count--;
      destinationFolder.files_count++;
    },
    moveMultipleFilesToFolder: (state, { payload }) => {
      const { expandedFolder, folders } = state;
      const { parentId, destinationId, movedEntities } = payload;

      const parentFolder = folders.find((f) => f.id === parentId);
      const destinationFolder = folders.find((f) => f.id === destinationId);

      for (const file of movedEntities) {
        expandedFolder.files = expandedFolder.files.filter((f) => f.id !== file.id);
        parentFolder.files_count--;
        expandedFolder.files_count--;
        destinationFolder.files_count++;
      }
    },
    deleteSingleFile: (state, { payload }) => {
      const { expandedFolder, folders } = state;
      const { deletedEntities } = payload;

      const file = deletedEntities[0];
      const parentFolder = folders.find((f) => f.id === expandedFolder.id);

      expandedFolder.files = expandedFolder.files.filter((f) => f.id !== file.id);
      expandedFolder.files_count--;
      parentFolder.files_count--;
    },
    deleteMultipleFiles: (state, { payload }) => {
      const { expandedFolder, folders } = state;
      const { deletedEntities } = payload;
    
      const parentFolder = folders.find((f) => f.id === expandedFolder.id);

      for (const file of deletedEntities) {
        expandedFolder.files = expandedFolder.files.filter((f) => f.id !== file.id);
        expandedFolder.files_count--;
        parentFolder.files_count--;
      }
    },
    deleteSingleFolder: (state, { payload }) => {
      const { deletedEntities } = payload;

      const folder = deletedEntities[0];
      state.folders = state.folders.filter((f) => f.id !== folder.id);
    },
    deleteMultipleFolders: (state, { payload }) => {
      const { deletedEntities } = payload;

      for (const folder of deletedEntities) {
        state.folders = state.folders.filter((f) => f.id !== folder.id);
      }
    },
    addActiveDownload: (state, { payload }) => {
      const { activeDownloads } = state;
      const item = payload;

      activeDownloads[item.id] = item;
    },
    removeActiveDownload: (state, { payload }) => {
      const { activeDownloads } = state;
      const { itemId } = payload;

      if (itemId in activeDownloads) {
        delete activeDownloads[itemId];
      }
    },
    updateActiveDownload: (state, { payload }) => {
      const { activeDownloads } = state;
      const { itemId, totalBytes, receivedBytes } = payload;
    
      if (itemId in activeDownloads) {
        activeDownloads[itemId]['receivedBytes'] = receivedBytes;
        if (!activeDownloads[itemId]['totalBytes']) {
          activeDownloads[itemId]['totalBytes'] = totalBytes;
        }
      }
    },
  },
  extraReducers: (folders) => {
    folders.addCase(fetchFormResponsesFolders.pending, (state) => {
      state.isLoading = true;
    });
    folders.addCase(fetchFormResponsesFolders.fulfilled, (state, { payload }) => {
      const { form_id, folders, error } = payload;
      state.isLoading = false;
      
      if (error) {
        state.isError = true;
        state.error = error && failedGetMsg;
        console.error('Files Fetch Error', error);
        fpLogging().logErrorToCloudReporting(error);
      } else {
        (state.formId = form_id), (state.folders = folders.sort((a, b) => new Date(b.created) - new Date(a.created)));
      }
    });
    folders.addCase(fetchFormResponsesFolders.rejected, (state, { payload }) => {
      const { error } = payload;
      state.isLoading = false;
      state.isError = true;
      state.error = error && failedGetMsg;
      console.error('Files Fetch Error', error);
      fpLogging().logErrorToCloudReporting(error);
    });
  },
});

const { reducer, actions } = fileManagerSlice;
export default reducer;
export const {
  renameFile,
  clearError,
  processEnd,
  createFolder,
  renameFolder,
  processStart,
  processError,
  changePageSize,
  fetchFilesFail,
  collapseFolder,
  fetchFilesStart,
  deleteSingleFile,
  fetchFilesSuccess,
  addActiveDownload,
  deleteSingleFolder,
  deleteMultipleFiles,
  removeActiveDownload,
  updateActiveDownload,
  deleteMultipleFolders,
  moveSingleFileToFolder,
  moveMultipleFilesToFolder,
} = actions;
